"use client"

import TimeLineSection from "@/components/tours/time-line"
import { Images } from "@/constants/images"
import { t } from "@/languages/i18n"
import { convertHtmlToArray, toNFC } from "@/lib/string-helper"
import { cn, updateImageSrc } from "@/lib/utils"
import { TourDetail } from "@/types/tour"
import { Switch } from "antd"
import Image from "next/image"
import { useCallback, useEffect, useRef, useState } from "react"

const HighlightTourDetail = ({ highlight, name }: { highlight?: string; name?: string }) => {
  const convertedHighlight = convertHtmlToArray(highlight)

  if (!convertedHighlight) return null

  return (
    <div className="bg-[#C9E4A5] p-6 rounded-xl flex flex-col gap-6">
      {convertedHighlight
        ?.filter(item => item !== "")
        ?.map((item, index) => (
          <div key={index} className="flex flex-row gap-3 items-start lg:items-center">
            <Image
              src={Images.checkRingRound ?? ""}
              alt={`${name ?? ""} - highlight ${index + 1}`}
              width={40}
              height={40}
              sizes="80vw"
              className="object-contain w-10 h-10"
            />
            <h3 dangerouslySetInnerHTML={{ __html: item }} />
          </div>
        ))}
    </div>
  )
}

const TabContent = ({ data }: { data?: TourDetail }) => {
  const [activeTab, setActiveTab] = useState("overview")
  const [isShowAll, setIsShowAll] = useState(false)
  const [timeLineActive, setTimeLineActive] = useState<string[]>([])
  const [isScrolledToTab, setIsScrolledToTab] = useState(false)

  const highlightRef = useRef<HTMLDivElement>(null)
  const overviewRef = useRef<HTMLDivElement>(null)
  const itineraryRef = useRef<HTMLDivElement>(null)
  const tourDetailRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleClickTab = (key: string) => {
    setActiveTab(key)
    const element = document && document?.getElementById(key)
    if (element) {
      const yOffset = -120
      const yPosition = element.getBoundingClientRect().top + window.pageYOffset + yOffset
      window.scrollTo({
        top: yPosition,
        behavior: "smooth",
      })
    }
  }

  const handleSetTimeLineActive = (value: string[]) => {
    setTimeLineActive(value)
    if (value.length === data?.itinerary?.time_line?.length) {
      setIsShowAll(true)
    } else {
      setIsShowAll(false)
    }
  }

  const handleChangeShowAll = (value: boolean) => {
    setIsShowAll(value)
    if (value) {
      setTimeLineActive(data?.itinerary?.time_line?.map((_, index) => index?.toString()) ?? [])
    } else {
      setTimeLineActive([])
    }
  }

  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const containerEl = containerRef.current
      const containerRect = containerEl.getBoundingClientRect()
      const containerTop = containerRect.top + window.scrollY
      const paddingTop = parseFloat(window.getComputedStyle(containerEl).paddingTop || "0") || 0

      const header = document.getElementById("site-header") as HTMLDivElement | null
      const headerHeight = header?.offsetHeight ?? 70

      const threshold = containerTop + paddingTop - headerHeight

      if (window.scrollY >= threshold) {
        setIsScrolledToTab(true)
        if (header) {
          header.style.transform = "translateY(-90px)"
          header.style.transition = "transform 0.3s ease"
        }
      } else {
        setIsScrolledToTab(false)
        if (header) {
          header.style.transform = "translateY(0)"
          header.style.transition = "transform 0.3s ease"
        }
      }
    }

    const sections = [
      { id: "overview", ref: overviewRef },
      { id: "highlight", ref: highlightRef },
      { id: "itinerary", ref: itineraryRef },
      { id: "tour_detail", ref: tourDetailRef },
    ]

    const scrollPosition = window.scrollY + 200 // Offset cho sticky header

    const sectionVisibility = sections
      .map(({ id, ref }) => {
        if (!ref.current) return { id, visibleHeight: 0 }

        const rect = ref.current.getBoundingClientRect()
        const elementTop = rect.top + window.scrollY
        const elementBottom = elementTop + rect.height

        const isInViewport = elementTop <= scrollPosition && elementBottom >= scrollPosition

        if (!isInViewport) return { id, visibleHeight: 0 }

        const visibleTop = Math.max(elementTop, scrollPosition)
        const visibleBottom = Math.min(elementBottom, scrollPosition + window.innerHeight - 200)
        const visibleHeight = Math.max(0, visibleBottom - visibleTop)

        return { id, visibleHeight }
      })
      .filter(section => section.visibleHeight > 0)

    const mostVisibleSection = sectionVisibility.reduce(
      (prev, current) => (current.visibleHeight > prev.visibleHeight ? current : prev),
      { id: "overview", visibleHeight: 0 },
    )

    setActiveTab(mostVisibleSection.id)
  }, [])

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true })

    handleScroll()

    return () => {
      window.removeEventListener("scroll", handleScroll)
      // Reset header styles when component unmounts
      const header = document.getElementById("site-header") as HTMLDivElement | null
      if (header) {
        header.style.transform = ""
        header.style.transition = ""
      }
    }
  }, [])

  if (!data) return null

  const tabs = [
    { label: t("Overview"), data: data?.overview, key: "overview" },
    { label: t("Highlight"), data: data?.highlights, key: "highlight" },
    { label: t("Itinerary"), data: data?.itinerary, key: "itinerary" },
    { label: t("Tour Detail"), data: data?.tour_detail, key: "tour_detail" },
  ].filter(tab => tab.data)

  const headingStyle = "text-[1.625rem] leading-[2.125rem] font-bold lg:text-[1.75rem] lg:leading-9"

  return (
    <div ref={containerRef} className="pt-16 lg:pt-32">
      {tabs.length > 0 && (
        <>
          <div
            className={`w-[calc(100%+2rem)] -mx-4 text-xl leading-[1.875rem] font-semibold text-[#242F3E] sticky bg-white z-20 lg:w-full lg:mx-0 transition-all duration-300 ${
              isScrolledToTab ? "top-0" : "top-[4.125rem] lg:top-[4.375rem]"
            }`}
          >
            <div
              className="flex flex-row gap-4 whitespace-nowrap overflow-x-auto lg:justify-between"
              style={{
                scrollbarWidth: "none",
              }}
            >
              {tabs.map((item, index) => (
                <p
                  key={item.label}
                  onClick={() => handleClickTab(item.key)}
                  className={`border-b-[2.5px] border-[#0B9444] py-3 w-fit transition-all duration-200 cursor-pointer ${
                    item.key === activeTab ? "" : "border-transparent"
                  } ${index === 0 ? "ml-4 lg:ml-0" : ""} ${index === tabs.length - 1 ? "mr-4 lg:mr-0" : ""}`}
                >
                  {item.label}
                </p>
              ))}
            </div>

            <div className="w-full border-b-[2.5px] border-[#6670851F] -mt-[2.5px]" />
          </div>

          <div className="pb-10 lg:pb-16" />
        </>
      )}

      {data?.overview && (
        <div id="overview" ref={overviewRef} className="flex flex-col gap-6 text-[#242F3E] pb-16">
          {data?.overview?.label && <h2 className={headingStyle}>{data?.overview?.label}</h2>}

          {data?.overview?.description && (
            <div
              className="text-base leading-[1.6875rem] font-normal"
              dangerouslySetInnerHTML={{ __html: toNFC(data?.overview?.description) }}
            />
          )}
        </div>
      )}

      {data?.highlights && (
        <div
          id="highlight"
          ref={highlightRef}
          className="text-base leading-[1.6875rem] font-normal text-[#242F3E] pb-16"
        >
          <h2 className={cn(headingStyle, "pb-6")}>{t("Why you'll love this tour")}</h2>
          <HighlightTourDetail highlight={data?.highlights} name={data?.name} />
        </div>
      )}

      {data.itinerary && (
        <div id="itinerary" ref={itineraryRef} className="pb-12">
          <div className="flex flex-row justify-between">
            <h2 className={headingStyle}>{t("Itinerary")}</h2>

            <div className="flex flex-row gap-[0.3125rem] items-center">
              <p
                className="text-base lg:text-2xl leading-7 font-normal text-[#4F4F4F] hover:text-[#3ab54b] hover:underline transition-all duration-200 cursor-pointer"
                onClick={() => handleChangeShowAll(!isShowAll)}
              >
                {t(isShowAll ? "Collapse" : "Show all")}
              </p>
              <Switch
                size="small"
                rootClassName="custom-switch-tour-detail"
                checked={isShowAll}
                onChange={handleChangeShowAll}
              />
            </div>
          </div>

          <div className="pt-6 flex flex-col gap-6 lg:flex-row">
            <div className="relative aspect-[0.652] w-full lg:aspect-[0.8745] lg:max-h-[32.875rem] lg:max-w-[28.75rem] overflow-hidden">
              <Image
                src={updateImageSrc(data?.itinerary?.image?.url)}
                alt={data?.itinerary?.image?.alternativeText || data?.itinerary?.image?.name || data.name || ""}
                fill
                sizes="(max-width: 768px) 100vw, 80vw"
                className="object-cover transition-all duration-200 hover:scale-105"
              />
            </div>

            {data?.itinerary?.time_line && data?.itinerary?.time_line?.length > 0 && (
              <TimeLineSection
                timeLine={data?.itinerary?.time_line}
                value={timeLineActive}
                setValue={handleSetTimeLineActive}
              />
            )}
          </div>
        </div>
      )}

      {data?.tour_detail && (
        <div id="tour_detail" ref={tourDetailRef} className="pb-12 lg:pb-[7.5rem]">
          <h2 className={headingStyle}>{t("Tour Detail")}</h2>
          <div className="pt-6 flex flex-col gap-6">
            <div
              className="text-base leading-[1.6875rem] font-normal text-[#242F3E]"
              dangerouslySetInnerHTML={{ __html: toNFC(data?.tour_detail) }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default TabContent
